import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, CheckCircle2, Sparkles, Loader2 } from 'lucide-react';
import { api } from '@/api/client';

export type Issue = {
  field: string;
  severity: 'error' | 'warning';
  message: string;
  source: 'rule' | 'ai';
};

// Normalized field shape both the rules and the AI validator understand.
export interface ValidatableOrder {
  customerName?: string;
  customerMobile?: string;
  customerAddress?: string;
  customerCity?: string;
  customerState?: string;
  customerPincode?: string;
  // total shipment weight (kg). B2B captures ONE weight for the whole shipment, not per box.
  totalWeight?: number;
  // a flat list of box dimensions; B2C has one, B2B has many
  dimensions?: Array<{ length?: number; breadth?: number; height?: number; weight?: number; qty?: number }>;
}

const digits = (v: any) => String(v ?? '').replace(/\D/g, '');

/**
 * Deterministic rule-based checks. These produce the HARD errors that block
 * submission; the AI pass only adds soft warnings on top.
 */
export function ruleCheck(orderType: 'B2C' | 'B2B', f: ValidatableOrder): Issue[] {
  const issues: Issue[] = [];
  const push = (field: string, severity: Issue['severity'], message: string) =>
    issues.push({ field, severity, message, source: 'rule' });

  // Customer name
  if (!String(f.customerName ?? '').trim()) push('customerName', 'error', 'Customer name is missing.');

  // Mobile — valid 10-digit Indian mobile
  const mob = digits(f.customerMobile);
  if (!mob) push('customerMobile', 'error', 'Customer mobile is missing.');
  else if (mob.length !== 10) push('customerMobile', 'error', `Customer mobile must be 10 digits (got ${mob.length}).`);
  else if (!/^[6-9]/.test(mob)) push('customerMobile', 'error', 'Customer mobile should start with 6, 7, 8 or 9.');

  // Address
  const addr = String(f.customerAddress ?? '').trim();
  if (!addr) push('customerAddress', 'error', 'Customer address is missing.');
  else if (addr.length < 10) push('customerAddress', 'warning', 'Customer address looks too short / incomplete.');
  else if (!/\d/.test(addr)) push('customerAddress', 'warning', 'Address has no house/building number — may be incomplete.');

  // City / State
  if (!String(f.customerCity ?? '').trim()) push('customerCity', 'error', 'City is missing.');
  if (!String(f.customerState ?? '').trim()) push('customerState', 'error', 'Service state is missing.');

  // Pincode — 6 digits
  const pin = digits(f.customerPincode);
  if (!pin) push('customerPincode', 'error', 'Pincode is missing.');
  else if (pin.length !== 6) push('customerPincode', 'error', `Pincode must be 6 digits (got ${pin.length}).`);

  // Dimensions / weight
  const dims = Array.isArray(f.dimensions) ? f.dimensions : [];
  const anyDimValue = dims.some(d => Number(d?.length) > 0 || Number(d?.breadth) > 0 || Number(d?.height) > 0 || Number(d?.weight) > 0);
  if (dims.length === 0 || !anyDimValue) {
    push('dimensions', 'warning', 'No weight / dimensions entered — charged weight may be wrong.');
  }
  dims.forEach((d, i) => {
    const tag = dims.length > 1 ? ` (box ${i + 1})` : '';
    const has = (v: any) => v !== undefined && v !== null && String(v) !== '';
    for (const [k, label] of [['length', 'Length'], ['breadth', 'Breadth'], ['height', 'Height'], ['weight', 'Weight']] as const) {
      const v = (d as any)[k];
      if (has(v) && !(Number(v) > 0)) push('dimensions', 'error', `${label}${tag} must be greater than 0.`);
    }
    // B2B captures L/B/H per box but a single Total Weight for the whole shipment (there is no
    // per-box weight field), so a box only needs its three dimensions; weight is checked at the
    // order level below. A partially-filled box (some but not all of L/B/H) is a hard error.
    if (orderType === 'B2B') {
      const filled = ['length', 'breadth', 'height'].filter(k => Number((d as any)[k]) > 0).length;
      if (filled > 0 && filled < 3) push('dimensions', 'error', `Box ${i + 1} is missing some of L/B/H.`);
    }
  });

  // B2B weight is a single shipment total (Total Weight), not per box.
  if (orderType === 'B2B' && !(Number(f.totalWeight) > 0)) {
    push('totalWeight', 'error', 'Total weight is missing or must be greater than 0.');
  }

  return issues;
}

function IssueRow({ it }: { it: Issue }) {
  const isErr = it.severity === 'error';
  return (
    <li className={`flex items-start gap-1.5 text-xs ${isErr ? 'text-rose-700' : 'text-amber-700'}`}>
      {isErr ? <AlertCircle size={13} className="mt-0.5 shrink-0" /> : <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
      <span>
        {it.message}
        {it.source === 'ai' && <span className="ml-1 align-middle text-[9px] uppercase px-1 py-0.5 rounded bg-violet-100 text-violet-700">AI</span>}
      </span>
    </li>
  );
}

/**
 * Live validation panel. Rule issues recompute on every field change; the AI
 * review is on-demand (one credit-cheap call) and merges soft warnings on top.
 */
export function OrderValidationPanel({ orderType, fields }: { orderType: 'B2C' | 'B2B'; fields: ValidatableOrder }) {
  const [aiIssues, setAiIssues] = useState<Issue[] | null>(null);
  const ruleIssues = useMemo(() => ruleCheck(orderType, fields), [orderType, fields]);

  const aiReview = useMutation({
    mutationFn: async () => (await api.post('/ai-chatbot/validate-order', { orderType, fields })).data as {
      ok: boolean; issues: Array<{ field: string; severity: 'error' | 'warning'; message: string }>;
    },
    onSuccess: (d) => {
      if (!d.ok) { setAiIssues([]); return; }
      // Drop AI issues that duplicate a rule message we already show.
      const seen = new Set(ruleIssues.map(r => r.message.toLowerCase()));
      setAiIssues(d.issues.filter(i => !seen.has(i.message.toLowerCase())).map(i => ({ ...i, source: 'ai' as const })));
    },
  });

  const all = [...ruleIssues, ...(aiIssues ?? [])];
  const errors = all.filter(i => i.severity === 'error');
  const warnings = all.filter(i => i.severity === 'warning');
  const clean = errors.length === 0 && warnings.length === 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          Order Validation
          {errors.length > 0 && <span className="text-xs px-2 py-0.5 rounded bg-rose-100 text-rose-700">{errors.length} error{errors.length > 1 ? 's' : ''}</span>}
          {errors.length === 0 && warnings.length > 0 && <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">{warnings.length} warning{warnings.length > 1 ? 's' : ''}</span>}
          {clean && <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Looks good</span>}
        </h3>
        <button type="button" onClick={() => aiReview.mutate()} disabled={aiReview.isPending}
          className="text-xs px-2.5 py-1 border border-violet-300 text-violet-700 rounded-lg hover:bg-violet-50 disabled:opacity-50 flex items-center gap-1.5">
          {aiReview.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI review
        </button>
      </div>
      {!clean && (
        <ul className="space-y-1">
          {errors.map((it, i) => <IssueRow key={`e${i}`} it={it} />)}
          {warnings.map((it, i) => <IssueRow key={`w${i}`} it={it} />)}
        </ul>
      )}
      {errors.length > 0 && <p className="text-[11px] text-slate-500 pt-1 border-t border-slate-100">Fix the errors above before saving. Warnings are advisory.</p>}
    </div>
  );
}
